import { Object as FabricObject } from 'fabric/fabric-impl';
import { MatchLayoutSettings } from 'modules/manager/domain/match-layout';
import { PlayerRankingSettings } from 'modules/manager/domain/player-stats';

/** GraphQL `MediaManagerTemplate` — camelCase, `objects` omitted on the org list. */
export interface MediaManagerTemplateGql {
  id: number;
  name: string;
  objects?: FabricObject[] | null;
  version: string;
  order: number;
  width: number;
  height: number;
  tournamentId: number | null;
  templateGroupId: number;
  sportType: string | null;
  templateType: string;
  mediaKind: string;
  isDefaultForKind: boolean;
  templateSubtypes: string[] | null;
  matchLayout?: MatchLayoutSettings | null;
  playerRanking?: PlayerRankingSettings | null;
  createdAt: string;
  updatedAt: string;
}

export interface GraphqlErrorShape {
  message: string;
  extensions?: { code?: number };
}

export interface GraphqlEnvelope<T> {
  data?: T;
  errors?: GraphqlErrorShape[];
}

const LIST_FIELDS = `
  id
  name
  version
  order
  width
  height
  tournamentId
  templateGroupId
  sportType
  templateType
  mediaKind
  isDefaultForKind
  templateSubtypes
  matchLayout
  playerRanking
  createdAt
  updatedAt
`;

const FULL_FIELDS = `
  ${LIST_FIELDS}
  objects
`;

export const MEDIA_MANAGER_TEMPLATES_QUERY = `#graphql
  query mediaManagerTemplates($tournamentId: Int!, $sportType: String, $templateGroupId: Int) {
    mediaManagerTemplates(tournamentId: $tournamentId, sportType: $sportType, templateGroupId: $templateGroupId) {
      ${LIST_FIELDS}
    }
  }
`;

export const MEDIA_MANAGER_PRESETS_QUERY = `#graphql
  query mediaManagerPresets($sportType: String) {
    mediaManagerPresets(sportType: $sportType) {
      ${FULL_FIELDS}
    }
  }
`;

export const MEDIA_MANAGER_TEMPLATE_QUERY = `#graphql
  query mediaManagerTemplate($id: Int!) {
    mediaManagerTemplate(id: $id) {
      ${FULL_FIELDS}
    }
  }
`;

export const CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION = `#graphql
  mutation createMediaManagerTemplate($input: CreateMediaManagerTemplateInput!) {
    createMediaManagerTemplate(input: $input) {
      ${FULL_FIELDS}
    }
  }
`;

export const UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION = `#graphql
  mutation updateMediaManagerTemplate($input: UpdateMediaManagerTemplateInput!) {
    updateMediaManagerTemplate(input: $input) {
      ${FULL_FIELDS}
    }
  }
`;

export const DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION = `#graphql
  mutation deleteMediaManagerTemplate($id: Int!) {
    deleteMediaManagerTemplate(id: $id) {
      status
    }
  }
`;

export const COPY_MEDIA_MANAGER_TEMPLATE_MUTATION = `#graphql
  mutation copyMediaManagerTemplate($input: CopyMediaManagerTemplateInput!) {
    copyMediaManagerTemplate(input: $input) {
      ${FULL_FIELDS}
    }
  }
`;

export const MEDIA_KIND_CATALOG_QUERY = `#graphql
  query mediaKindCatalog($sportType: SportType) {
    mediaKindCatalog(sportType: $sportType) {
      kind
      title
      templateType
      supported
    }
  }
`;

export interface MediaKindInfoGql {
  kind: string;
  title: string;
  templateType: string;
  supported: boolean;
}

export function unwrapGraphqlData<T>(envelope: GraphqlEnvelope<T>): T {
  if (envelope.errors && envelope.errors.length > 0) {
    const first = envelope.errors[0];
    const error = new Error(first.message) as Error & {
      extensions?: { code?: number };
      data?: GraphqlErrorShape[];
    };
    error.extensions = first.extensions;
    error.data = envelope.errors;
    throw error;
  }
  if (envelope.data === undefined || envelope.data === null) {
    throw new Error('Empty GraphQL response');
  }
  return envelope.data;
}
